<template>
<div v-if="isFetchData" class="p-3 pt-0">
<h1 class="px-3">
	<span v-if="action == 'create'" class="fs-5">Створення нової організації</span>
	<span v-if="action == 'update'" class="fs-5">{{organization.title}}</span>
</h1>

<Form
	@submit="onSubmit"
	@invalid-submit="console.log"
	:validation-schema="schema"
	:initial-values="formValues"
	v-slot="{ errors, meta: meta, values }"
>
	<div class="row form-row my-5">
		<div class="form-group col-auto input-min-width">
			<label>Назва&nbsp;*</label>
			<Field
				name="title"
				type="text"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors.title }"
			/>
			<div class="invalid-feedback">{{errors.title}}</div>
		</div>
	</div>
	
	<div class="row form-row mt-5 mb-1">
		<div class="form-group col-auto input-min-width">
			<label>Найменування адміністративної ланки 2-го рівня&nbsp;*</label>
			<Field
				name="department_title"
				type="text"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors.department_title }"
			/>
			<div class="invalid-feedback">{{errors.department_title}}</div>
		</div>
	</div>
	<div class="row form-row mt-2 mb-3">
		<div class="form-group col-auto input-min-width mt-1">
			<aside>Адміністративні ланки 2-го рівня</aside>
			<FieldArray
				name="departments"
				v-slot="{ fields, push, remove }"
			>
				<aside v-if="fields.length == 0" class="text-body-secondary fst-italic">
					Список ланок пустий. Своріть ланку натиснувши кнопку нижче.
				</aside>
				<fieldset
					class="py-1"
					v-for="(field, idx) in fields"
					:key="field.key"
				>
					<div class="row align-items-center">
						<div class="col-10 pe-1">
							<Field
								:id="`title_${idx}`"
								:name="`departments[${idx}].title`"
								class="form-control form-control-sm"
							/>
						</div>
						<!-- Delete item button -->
						<div class="col-1 mx-1 px-0">
							<button
								type="button"
								class="btn btn-sm btn-outline-dark p-1 mx-1"
								style="line-height: 14px;"
								@click="remove(idx)"
							>
								<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-trash3" viewBox="0 0 16 16">
									<path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5ZM11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0H11Zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5h9.916Zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47ZM8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5Z"/>
								</svg>
							</button>
						</div>

						<ErrorMessage
							:name="`departments[${idx}].title`"
							class="col-10 pt-1 pb-2 text-danger"
						/>
					</div>
				</fieldset>

				<!-- Add department button -->
				<button
					type="button"
					class="btn btn-sm btn-outline-dark p-1 pe-2"
					style="line-height: 14px;"
					@click="push(this.emptyDepartment());"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="16"
						height="16"
						fill="currentColor"
						class=""
						viewBox="0 0 16 16"
						style="width: 1em; height: 1em;"
					>
						<path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
					</svg>
					Додати
				</button>
			</FieldArray>
		</div>
	</div>

	<div class="row form-row mt-5 mb-1">
		<div class="form-group col-auto input-min-width">
			<label>Найменування адміністративної ланки 3-го рівня&nbsp;*</label>
			<Field
				name="sector_title"
				type="text"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors.sector_title }"
			/>
			<div class="invalid-feedback">{{errors.sector_title}}</div>
		</div>
	</div>
	<!-- Accordion with sector forms for every department in organization -->
	<div class="row form-row accordion accordion-flush mt-2 mb-3" id="sectors-accordion">
		<div class="form-group col-auto input-min-width mt-1 me-5">
			<aside>Адміністративні ланки 3-го рівня</aside>
			<div 
				v-for="dep in values.departments"
				:key="dep.id"
				class="accordion-item me-0"
			>
				<aside class="accordion-header">
					<button
						class="accordion-button collapsed p-1"
						style="line-height: 1.5; font-size: 0.875rem;"
						type="button"
						data-bs-toggle="collapse"
						:data-bs-target="`#department-${dep.id}-sectors`"
						aria-expanded="false"
						:aria-controls="`department-${dep.id}-sectors`"
					>
						{{ dep.title }}
					</button>
				</aside>
				<div
					:id="`department-${dep.id}-sectors`"
					class="accordion-collapse collapse"
					data-bs-parent="#sectors-accordion"
				>
					<div class="accordion-body px-0 ps-3">
						<FieldArray
							:name="`departments[${departmentObjIndex(values, dep.id)}].sectors`"
							v-slot="{ fields, push, remove }"
						>
							<aside v-if="fields.length == 0" class="text-body-secondary fst-italic">
								Список ланок пустий.<br>Своріть ланку натиснувши кнопку нижче.
							</aside>
							<fieldset
								class="py-1"
								v-for="(field, idx) in fields"
								:key="field.key"
							>
								<div class="row align-items-center">
									<div class="col-10 pe-1">
										<Field
											:id="`department${dep.id}_title_${idx}`"
											:name="`departments[${departmentObjIndex(values, dep.id)}].sectors[${idx}].title`"
											class="form-control form-control-sm"
										/>
									</div>

									<!-- Delete item button -->
									<div class="col-1 mx-1 px-0">
										<button
											type="button"
											class="btn btn-sm btn-outline-dark p-1 mx-1"
											style="line-height: 14px;"
											@click="remove(idx)"
										>
											<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-trash3" viewBox="0 0 16 16">
												<path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5ZM11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0H11Zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5h9.916Zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47ZM8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5Z"/>
											</svg>
										</button>
									</div>

									<ErrorMessage
										:name="`departments[${departmentObjIndex(values, dep.id)}].sectors[${idx}].title`"
										class="col-10 pt-1 pb-2 text-danger"
									/>
								</div>
							</fieldset>

							<!-- Add department button -->
							<button
								type="button"
								class="btn btn-sm btn-outline-dark p-1 pe-2"
								style="line-height: 14px;"
								@click="push(this.emptySector());"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="16"
									height="16"
									fill="currentColor"
									class=""
									viewBox="0 0 16 16"
									style="width: 1em; height: 1em;"
								>
									<path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
								</svg>
								Додати
							</button>
						</FieldArray>
					</div>
				</div>
			</div>
		</div>
	</div>

	<!-- <div class="row form-row mt-5 mb-1">
		<div class="form-group col-auto input-min-width">
			<label>Газопроводи&nbsp;/&nbsp;нафтопроводи</label>
			<Field
				name="pip"
				type="text"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors.pip }"
				disabled
			/>
			<div class="invalid-feedback">{{errors.pip}}</div>
		</div>
	</div> -->
	<div class="row form-row mt-2 mb-3">
		<div class="form-group col-auto input-min-width mt-1">
			<aside>Газопроводи&nbsp;/&nbsp;нафтопроводи</aside>
			<FieldArray
				name="pipelines"
				v-slot="{ fields, push, remove }"
			>
				<aside v-if="fields.length == 0" class="text-body-secondary fst-italic">
					Список пустий. Своріть трубопровід натиснувши кнопку нижче.
				</aside>
				<fieldset
					class="py-1"
					v-for="(field, idx) in fields"
					:key="field.key"
				>
					<div class="row align-items-center">
						<div class="col-10 pe-1">
							<Field
								:id="`title_${idx}`"
								:name="`pipelines[${idx}].title`"
								class="form-control form-control-sm"
							/>
						</div>
						<!-- Delete item button -->
						<div class="col-1 mx-1 px-0">
							<button
								type="button"
								class="btn btn-sm btn-outline-dark p-1 mx-1"
								style="line-height: 14px;"
								@click="remove(idx)"
							>
								<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-trash3" viewBox="0 0 16 16">
									<path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5ZM11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0H11Zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5h9.916Zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47ZM8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5Z"/>
								</svg>
							</button>
						</div>

						<ErrorMessage
							:name="`pipelines[${idx}].title`"
							class="col-10 pt-1 pb-2 text-danger"
						/>
					</div>
				</fieldset>

				<!-- Add pipeline button -->
				<button
					type="button"
					class="btn btn-sm btn-outline-dark p-1 pe-2"
					style="line-height: 14px;"
					@click="push(this.emptyPipeline());"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="16"
						height="16"
						fill="currentColor"
						class=""
						viewBox="0 0 16 16"
						style="width: 1em; height: 1em;"
					>
						<path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
					</svg>
					Додати
				</button>
			</FieldArray>
		</div>
	</div>
	<!-- Erros {{ errors }} <br> -->
	<!-- Values {{ formValues }} -->

	<div class="row form-row my-3">
		<span class="form-text text-decoration-underline">
			*&nbsp;поля обов'язкові для заповнення
		</span>
	</div>

	<div class="row form-row my-3">
		<span class="form-text fst-italic">
			<span 
				v-if="executionResultMessage.length > 0"
				class="fs-5"
				:class="{'text-success': executionOk, 'text-danger': !executionOk}"
			>
				{{ executionResultMessage }}
			</span>
		</span>
	</div>
	
	<div class="row form-row mt-5">
		<div class="form-group col-auto ms-0 me-auto">
			&nbsp;
			<button
				v-if="action == 'update'"
				type="button"
				class="btn btn-danger btn-sm"
				@click="deleteConfirmationPopup = true"
				:disabled="accountStore.organization.role != 'primary'"
			>
				Видалити
			</button>
			<confirmation-popup
				v-model:show="deleteConfirmationPopup"
				@confirm="deleteOrganization"
			>
				<template v-slot:body>
					<p>
						Ви впевнені що хочете <b>видалити</b> організацію?
					</p>
				</template>
			</confirmation-popup>
		</div>

		<div class="form-group col-auto">&nbsp;</div>

		<div class="form-group col-auto ms-auto me-2">
			<button
				type="button"
				class="btn btn-secondary btn-sm mx-2"
				@click="onCancel"
			>
				Відмінити
			</button>
		</div>
		<div class="form-group col-auto me-0">
			<button
				type="submit"
				class="btn btn-primary btn-sm"
				:disabled="!meta.valid"
			>
				{{ action == 'create' ? 'Створити' : '' }}
				{{ action == 'update' ? 'Зберегти' : '' }}
			</button>
			<confirmation-popup
				v-model:show="updateConfirmationPopup"
				@confirm="action === 'create' ? create() : update()"
			>
				<template v-slot:body>
					<p v-if="action === 'create'">
						Ви впевнені що хочете <b>створити</b> організацію?
					</p>
					<p v-if="action === 'update'">
						Ви впевнені що хочете змінити організацію?<br>
						Зміни в адміністративних ланках будуть відображені в станціях.
					</p>
				</template>
			</confirmation-popup>
		</div>
	</div>
</Form>
</div>
</template>
	
	
<script>
import { useAccontStore } from '@/stores/AccountStore.js'
import { useAccounts } from '@/composables/accounts.js'
import { nameRegex } from "@/models/objectPrototype.js"
import { Station } from '@/models/station.js'
import { RESOURCE_NAMES, BLANK_ORGANIZATION_ID,
	readResourceItem, readResources, createResourceItem,
	updateResourceItem, deleteResourceItem,
} from '@/api/api.js'
import { capitalizeFirstLetter, createUuidv4 } from '@/helpers.js'
import ConfirmationPopup from '@/components/ConfirmationPopup.vue'
import { DEPARTMENT_TITLE, SECTOR_TITLE, } from '@/app.conf'
// Validation
// https://jasonwatmore.com/post/2020/10/01/vue-3-veevalidate-form-validation-example
import { Form, Field, ErrorMessage, FieldArray,  } from 'vee-validate';
// https://github.com/jquense/yup/tree/a58f02d2f6164c46a9757a818eebac582f7a441c#boolean
import * as Yup from 'yup';


export default {
	waitAfterExecuteSuccessMs: 2100,
	wrongInputPromt: 'Не відповідає вимогам',
	requiredPromt: 'Обов\'язкове поле',
	overMinLen: 'Довжина меньше мінімальної',
	overMaxLen: 'Довжина більше максимальної',

	components: {
		Form,
		Field,
		ErrorMessage,
		FieldArray,
		ConfirmationPopup,
	},

	props: {
	},

	emits: {
	},

	setup() {
		const accountStore = useAccontStore()
		const { accountSchema, allAccountsFiltered } = useAccounts()

		return {
			accountStore,
			accountSchema,
			allAccountsFiltered
		}
	},

	data() {
		const schema = Yup.object().shape({
			title: Yup.string()
				.typeError(this.$options.wrongInputPromt)
				.required(this.$options.requiredPromt)
				.min(1, this.$options.overMinLen)
				.max(182, this.$options.overMaxLen)
				.matches(nameRegex,
					'Може містити тільки знаки згідно правил написання найменування юридичної особи'),
			department_title: Yup.string()
				.typeError(this.$options.wrongInputPromt)
				.required(this.$options.requiredPromt)
				.min(1, this.$options.overMinLen)
				.max(12, this.$options.overMaxLen)
				.matches(/^[a-zA-Z0-9а-яА-ЯґҐєЄіІїЇўЎёЁ() -]*$/,
					'Може містити тільки букви і цифри'),
			sector_title: Yup.string()
				.typeError(this.$options.wrongInputPromt)
				.required(this.$options.requiredPromt)
				.min(1, this.$options.overMinLen)
				.max(12, this.$options.overMaxLen)
				.matches(/^[a-zA-Z0-9а-яА-ЯґҐєЄіІїЇўЎёЁ() -]*$/,
					'Може містити тільки букви і цифри'),
			departments: Yup.array()
				.of(
					Yup.object().shape({
					id: Yup.string()
						.min(36, this.$options.overMinLen)
						.max(36, this.$options.overMaxLen)
						.required(),
					title: Yup.string()
						.min(1, this.$options.overMinLen)
						.max(64, this.$options.overMaxLen)
						.required(),
					sectors: Yup.array()
						.of(
							Yup.object().shape({
							id: Yup.string()
								.min(36, this.$options.overMinLen)
								.max(36, this.$options.overMaxLen)
								.required(),
							title: Yup.string()
								.min(1, this.$options.overMinLen)
								.max(64, this.$options.overMaxLen)
								.required(),
							}),
						),
					}),
				),
			pipelines: Yup.array()
				.of(
					Yup.object().shape({
					id: Yup.string()
						.min(36, this.$options.overMinLen)
						.max(36, this.$options.overMaxLen)
						.required(),
					title: Yup.string()
						.min(1, this.$options.overMinLen)
						.max(64, this.$options.overMaxLen)
						.required(),
					})
				),
		});

		return {
			// Organization ID
			organizationId: null,
			// Organization object
			organization: {},
			//
			isFetchData: false,
			//
			updateConfirmationPopup: false,
			deleteConfirmationPopup: false,
			//------------------ Form
			schema,
			formValues: {},
			//------------------
			//  
			executionResultMessage: '',
			executionOk: null,
		}
	},

	async created() {
		// Read id from url
		try {
			this.organizationId = this.$route.params.id
		} catch {
			console.error("On organization id read.")
			return
		}
		
		// If form action is create organization
		if (this.action == 'create') {
			// Read blank organization
			this.formValues = await this.blankOrganization()
			// Set default values
			this.formValues.department_title = DEPARTMENT_TITLE
			this.formValues.sector_title = SECTOR_TITLE
		// If form action is update organization
		} else if (this.action == 'update') {
			this.organization = await readResourceItem(
				RESOURCE_NAMES.organizations,
				this.organizationId,
				this.accountStore.userLogin,
				this.accountStore.userPassword,
			).then(response => {
				return response
			}).catch(error => {
				console.error(`on load organization from server: "${error}".`)
				return null;	
			})

			if (this.organization === null) {
				return
			}

			// copy to form initial prop without service values
			Object.entries(this.organization).forEach(([key, value]) => {
				//console.log("key", key, "value", value)
				if (key[0] != "_") {
					this.formValues[key] = value
				}
			})
		}

		this.isFetchData = true
	},

	async mounted() {
		this.blankOrganization()
	},

	methods: {
		onCancel() { this.goBackAfterTimeout(10) },

		onSubmit(values) {
			// display form values on success
			//console.log("Form: ", values);

			// Update form values
			this.formValues = values
			
			this.updateConfirmationPopup = true
		},

		// Create organization and close popup
		async create() {
			this.executionResultMessage = ''

			// Check if the name of organization is not unique
			//
			const organizations = await this.readOrganizations()
			if (organizations === null) {
				this.executionResultMessage = 
					`Організацію не створено: неможливо зв'язатись з сервером.`
				this.executionOk = false
			}

			for (const org of organizations) {
				if (org.title == this.formValues.title) {
					this.executionResultMessage = 
						`Організацію не створено: вже існує організація з точно такою назвою.`
					this.executionOk = false
					return
				}
			}

			const payload = { ... this.formValues }

			// Request server to create account
			createResourceItem(
				RESOURCE_NAMES.organizations,
				this.accountStore.userLogin,
				this.accountStore.userPassword,
				payload
			)
			.then((/*result*/) => {
				//console.log("create account result = ", result)
				this.executionResultMessage = `Організацію створено.`
				this.executionOk = true

				this.goBackAfterTimeout()
			}).catch(err => {
				console.error("on create organization", err)
				this.executionResultMessage = 
					`Організацію не створено: сервер повернув помилку.`
				this.executionOk = false
			})
		},

		// Update organization
		async update() {
			this.executionResultMessage = ''

			// Check if the name of organization is not unique
			//
			const organizations = await this.readOrganizations()
			if (organizations === null) {
				this.executionResultMessage = 
					`Організацію не відредаговано: неможливо зв'язатись з сервером.`
				this.executionOk = false
			}

			for (const org of organizations) {
				if (org.title == this.formValues.title) {
					this.executionResultMessage = 
						`Організацію не відредаговано: вже існує організація з точно такою назвою.`
					this.executionOk = false
					return
				}
			}

			// Request server to update account
			updateResourceItem(
				RESOURCE_NAMES.organizations,
				this.accountStore.userLogin,
				this.accountStore.userPassword,
				this.organization._id,
				this.organization._etag,
				this.formValues
			).then(result => {
				//console.log("update account result = ", result)
				if (result._status == 'OK') {
					this.executionResultMessage = `Організацію відредаговано.`
					this.executionOk = true

					this.goBackAfterTimeout()
				}
			}).catch(err => {
				console.error("on update organization", err)
				this.executionResultMessage = 
					`Організацію не відредаговано: сервер повернув помилку.`
				this.executionOk = false
			})
		},

		async deleteOrganization() {
			this.executionResultMessage = ''

			// Check if organization have users
			const users = this.allAccountsFiltered({
				organization: this.organization[this.accountSchema.id.key]
			})
			if (users.length > 0) {
				this.executionResultMessage = 'Організація що має користувачів, не може бути видалена.'
				this.executionOk = false
				return
			}
			
			// Check if organization have stations
			const stations = await readResources(
				RESOURCE_NAMES.stationPassports,
				this.accountStore.userLogin,
				this.accountStore.userPassword,
				[`where={"${Station.passportKeys.orgId}": "${this.organization._id}"}`]
			)
			if (stations.length > 0) {
				this.executionResultMessage = 'Організація що має станції, не може бути видалена.'
				this.executionOk = false
				return
			}

			// Delete organization
			deleteResourceItem(
				RESOURCE_NAMES.organizations,
				this.accountStore.userLogin,
				this.accountStore.userPassword,
				this.organization._id,
				this.organization._etag
			).then((/*result*/) => {
				//console.log("delete account result = ", result)
				this.executionResultMessage = 'Організацію видалено.'
				this.executionOk = true

				this.goBackAfterTimeout()
			}).catch(err => {
				console.error("on delete organization", err)
				this.executionResultMessage = 
					`Організацію не видалено: сервер повернув помилку.`
				this.executionOk = false
			})
		},

		async readOrganizations() {
			const organizations = await readResources(
				RESOURCE_NAMES.organizations,
				this.accountStore.userLogin,
				this.accountStore.userPassword,
			)
			if (!organizations || organizations.length == 0) {
				return null
			}
			
			return organizations
		},

		departmentObjIndex(formData, depId) {
			let index = null
			formData.departments.forEach((dep, i) => {
				if (dep.id == depId) {
					index = i
				}
			})
			return index
		},

		emptyDepartment() {
			return { id: createUuidv4(), title: '', sectors: [] }
		},
		
		emptySector() {
			return { id: createUuidv4(), title: '' }
		},

		emptyPipeline() {
			return { id: createUuidv4(), title: '' }
		},

		async blankOrganization() {
			// Read blank organizxation from server
			return await readResourceItem(
				RESOURCE_NAMES.organizations,
				BLANK_ORGANIZATION_ID,
				this.accountStore.userLogin,
				this.accountStore.userPassword,
			).then(response => {
				delete response._items
				delete response._id
				delete response._created
				delete response._updated
				delete response._etag
				delete response._links

				return response
			}).catch(error => {
				console.error(`Error on read blank organization: "${error}".`)
				return new Object()
			})
		},

		goBackAfterTimeout(timeout = this.$options.waitAfterExecuteSuccessMs) {
			setTimeout(
				() => {
					// step back
					this.$router.go(-1)
				},
				timeout
			);
		}
	},

	computed: {
		capitalizeFirstLetter() { return capitalizeFirstLetter },
		
		action() { return this.organizationId == 'new' ? 'create' : 'update'; },
	},
};
</script>


<style scoped>
.input-min-width {
	min-width: 64ch;
}
</style>
	